import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Avatar,
  Divider,
  Rating,
  Alert,
  Paper
} from '@mui/material';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import StarIcon from '@mui/icons-material/Star';
import LanguageIcon from '@mui/icons-material/Language';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { api } from '../services/api';

// Defined theme colors matching the plan requirements
const THEMES: Record<string, {
  primary: string;
  primaryHover: string;
  bg: string;
  text: string;
  secondaryText: string;
  cardBg: string;
  cardBorder: string;
  btnText: string;
  badgeBg: string;
  badgeText: string;
}> = {
  default: {
    primary: '#1967D2',
    primaryHover: '#1556b3',
    bg: '#f8fafc',
    text: '#2b3154',
    secondaryText: '#7a829a',
    cardBg: '#ffffff',
    cardBorder: '#ecedf2',
    btnText: '#ffffff',
    badgeBg: 'rgba(25, 103, 210, 0.08)',
    badgeText: '#1967D2'
  },
  navy: {
    primary: '#1e293b',
    primaryHover: '#0f172a',
    bg: '#f1f5f9',
    text: '#0f172a',
    secondaryText: '#475569',
    cardBg: '#ffffff',
    cardBorder: '#e2e8f0',
    btnText: '#ffffff',
    badgeBg: 'rgba(30, 41, 59, 0.08)',
    badgeText: '#1e293b'
  },
  green: {
    primary: '#0f5132',
    primaryHover: '#0a3622',
    bg: '#f0fdf4',
    text: '#0f5132',
    secondaryText: '#166534',
    cardBg: '#ffffff',
    cardBorder: '#d1e7dd',
    btnText: '#ffffff',
    badgeBg: 'rgba(15, 81, 50, 0.08)',
    badgeText: '#0f5132'
  },
  red: {
    primary: '#842029',
    primaryHover: '#5c161c',
    bg: '#fef2f2',
    text: '#111827',
    secondaryText: '#991b1b',
    cardBg: '#ffffff',
    cardBorder: '#f8d7da',
    btnText: '#ffffff',
    badgeBg: 'rgba(132, 32, 41, 0.08)',
    badgeText: '#842029'
  },
  dark: {
    primary: '#3b82f6',
    primaryHover: '#2563eb',
    bg: '#0f172a',
    text: '#f8fafc',
    secondaryText: '#94a3b8',
    cardBg: '#1e293b',
    cardBorder: '#334155',
    btnText: '#ffffff',
    badgeBg: 'rgba(59, 130, 246, 0.15)',
    badgeText: '#3b82f6'
  },
  orange: {
    primary: '#c2410c',
    primaryHover: '#9a3412',
    bg: '#fff7ed',
    text: '#431407',
    secondaryText: '#c2410c',
    cardBg: '#ffffff',
    cardBorder: '#ffedd5',
    btnText: '#ffffff',
    badgeBg: 'rgba(194, 65, 12, 0.08)',
    badgeText: '#c2410c'
  }
};

export default function PublicInstructorProfile() {
  const { idOrSubdomain } = useParams<{ idOrSubdomain: string }>();
  const navigate = useNavigate();
  const [profile, setProfile] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Form Lead State
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [transmission, setTransmission] = useState('Manual');
  const [experienceLevel, setExperienceLevel] = useState('Beginner');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPublicProfile = async () => {
      try {
        setLoading(true);
        setError(null);
        if (!idOrSubdomain) {
          setError('No instructor identifier specified.');
          setLoading(false);
          return;
        }

        const res = await api.getPublicProfile(idOrSubdomain);
        if (res.data) {
          setProfile(res.data);
        } else {
          setError('Instructor profile could not be found.');
        }
      } catch (err: any) {
        console.error('Error fetching public profile:', err);
        setError(err.response?.data?.message || 'Instructor profile could not be found.');
      } finally {
        setLoading(false);
      }
    };

    fetchPublicProfile();
  }, [idOrSubdomain]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error || !profile) {
    return (
      <Container maxWidth="sm" sx={{ py: 10, textAlign: 'center' }}>
        <Alert severity="error" sx={{ mb: 4, borderRadius: 3 }}>
          {error || 'Instructor profile not found.'}
        </Alert>
        <Button variant="contained" onClick={() => window.location.href = '/'}>
          Return to Home
        </Button>
      </Container>
    );
  }

  // Active theme based on instructor preference
  const themeKey = (profile.themeName || 'default').toLowerCase();
  const theme = THEMES[themeKey] || THEMES.default;

  const averageRating = profile.reviews && profile.reviews.length > 0
    ? (profile.reviews.reduce((acc: number, r: any) => acc + r.rating, 0) / profile.reviews.length).toFixed(1)
    : 'New';

  const handleInquirySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      setSubmitSuccess(null);
      setSubmitError(null);

      await api.submitPublicInquiry({
        instructorId: profile.id,
        fullName,
        email,
        phone,
        message,
        preferredTransmission: transmission,
        experienceLevel
      });

      setSubmitSuccess('Your booking inquiry has been submitted! The instructor will review it and contact you shortly.');
      setFullName('');
      setEmail('');
      setPhone('');
      setMessage('');
    } catch (err: any) {
      console.error('Error submitting public inquiry:', err);
      setSubmitError(err.response?.data?.message || 'Failed to submit inquiry. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ bgcolor: theme.bg, minHeight: '100vh', py: { xs: 4, md: 8 }, color: theme.text }}>
      <Container maxWidth="lg">
        {profile.isClaimed === false && (
          <Alert 
            severity="warning" 
            action={
              <Button color="inherit" size="small" variant="outlined" onClick={() => navigate('/claim-profile')} sx={{ fontWeight: 'bold', textTransform: 'none' }}>
                Claim Profile
              </Button>
            }
            sx={{ mb: 4, borderRadius: '16px', fontWeight: '500' }}
          >
            This profile is unclaimed. Are you this instructor? Claim your profile now to manage your bookings and customize your theme.
          </Alert>
        )}
        {/* Header Cover Banner */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, md: 6 },
            borderRadius: '24px',
            background: `linear-gradient(135deg, ${theme.primary} 0%, ${theme.primaryHover} 100%)`,
            color: '#ffffff',
            mb: 4,
            position: 'relative',
            overflow: 'hidden'
          }}
        >
          {/* Subtle background decoration */}
          <Box
            sx={{
              position: 'absolute',
              top: -50,
              right: -50,
              width: 250,
              height: 250,
              borderRadius: '50%',
              background: 'rgba(255,255,255,0.05)',
              pointerEvents: 'none'
            }}
          />

          <Grid container spacing={4} alignItems="center">
            <Grid size={{ xs: 12, md: 3 }} display="flex" justifyContent={{ xs: 'center', md: 'flex-start' }}>
              <Avatar
                src={profile.profileImageUrl || ''}
                alt={`${profile.firstName} ${profile.lastName}`}
                sx={{
                  width: 150,
                  height: 150,
                  border: '6px solid rgba(255,255,255,0.2)',
                  boxShadow: '0 8px 30px rgba(0,0,0,0.15)'
                }}
              >
                {(profile.firstName?.[0] || '') + (profile.lastName?.[0] || '')}
              </Avatar>
            </Grid>
            <Grid size={{ xs: 12, md: 9 }} sx={{ textAlign: { xs: 'center', md: 'left' } }}>
              <Typography variant="h3" component="h1" sx={{ fontWeight: '800', mb: 1.5 }}>
                ADI {profile.firstName} {profile.lastName}
              </Typography>
              
              <Box display="flex" flexWrap="wrap" justifyContent={{ xs: 'center', md: 'flex-start' }} alignItems="center" gap={2} sx={{ mb: 2 }}>
                <Box display="flex" alignItems="center" sx={{ bgcolor: 'rgba(255,255,255,0.15)', px: 2, py: 0.5, borderRadius: '20px' }}>
                  <StarIcon sx={{ color: '#ffb300', mr: 0.5 }} />
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                    {averageRating} ({profile.reviews?.length || 0} Reviews)
                  </Typography>
                </Box>

                <Box display="flex" alignItems="center" sx={{ bgcolor: 'rgba(255,255,255,0.15)', px: 2, py: 0.5, borderRadius: '20px' }}>
                  <DirectionsCarIcon sx={{ mr: 0.5 }} />
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                    {profile.transmissionType}
                  </Typography>
                </Box>

                <Box display="flex" alignItems="center" sx={{ bgcolor: 'rgba(255,255,255,0.15)', px: 2, py: 0.5, borderRadius: '20px' }}>
                  <LanguageIcon sx={{ mr: 0.5 }} />
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                    {profile.subdomain ? `${profile.subdomain}.adilesson.co.uk` : 'adiLessons Verified'}
                  </Typography>
                </Box>
              </Box>

              <Typography variant="h6" sx={{ opacity: 0.9, fontWeight: '500' }}>
                Hourly rate: <span style={{ fontWeight: '800', fontSize: '24px' }}>£{profile.baseHourlyRate?.toFixed(2)}</span>
              </Typography>
            </Grid>
          </Grid>
        </Paper>

        {/* Content Body */}
        <Grid container spacing={4}>
          {/* Left Column: Bio, Details, Reviews */}
          <Grid size={{ xs: 12, md: 7 }}>
            <Card
              elevation={0}
              sx={{
                p: 4,
                borderRadius: '20px',
                border: `1px solid ${theme.cardBorder}`,
                bgcolor: theme.cardBg,
                mb: 4
              }}
            >
              <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2, borderBottom: `2px solid ${theme.primary}`, pb: 1, display: 'inline-block' }}>
                About Me
              </Typography>
              <Typography variant="body1" sx={{ lineHeight: 1.8, whiteSpace: 'pre-line' }}>
                {profile.profileInfo || `${profile.firstName} is a professional driving instructor listed on adiLessons, dedicated to helping students pass their driving test with confidence.`}
              </Typography>
            </Card>

            {/* Vehicle Photos */}
            {profile.vehicleImageUrls && profile.vehicleImageUrls.split(';').filter(Boolean).length > 0 && (
              <Card
                elevation={0}
                sx={{
                  p: 4,
                  borderRadius: '20px',
                  border: `1px solid ${theme.cardBorder}`,
                  bgcolor: theme.cardBg,
                  mb: 4
                }}
              >
                <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 3, borderBottom: `2px solid ${theme.primary}`, pb: 1, display: 'inline-block' }}>
                  Training Vehicle
                </Typography>
                <Grid container spacing={2}>
                  {profile.vehicleImageUrls.split(';').filter(Boolean).map((url: string, index: number) => (
                    <Grid size={{ xs: 12, sm: 6 }} key={index}>
                      <Box
                        component="img"
                        src={url}
                        alt="Training vehicle"
                        sx={{
                          width: '100%',
                          height: 180,
                          objectFit: 'cover',
                          borderRadius: '12px',
                          border: `1px solid ${theme.cardBorder}`
                        }}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Card>
            )}

            {/* Reviews Section */}
            <Card
              elevation={0}
              sx={{
                p: 4,
                borderRadius: '20px',
                border: `1px solid ${theme.cardBorder}`,
                bgcolor: theme.cardBg
              }}
            >
              <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 3, borderBottom: `2px solid ${theme.primary}`, pb: 1, display: 'inline-block' }}>
                Pupil Reviews ({profile.reviews?.length || 0})
              </Typography>

              {(!profile.reviews || profile.reviews.length === 0) ? (
                <Box textAlign="center" py={4}>
                  <ThumbUpIcon sx={{ fontSize: 40, color: theme.secondaryText, mb: 1.5, opacity: 0.5 }} />
                  <Typography variant="body1" sx={{ color: theme.secondaryText }}>
                    No reviews yet. Be one of the first pupils to pass with {profile.firstName}!
                  </Typography>
                </Box>
              ) : (
                <Box display="flex" flexDirection="column" gap={3}>
                  {profile.reviews.map((review: any) => (
                    <Box key={review.id} sx={{ p: 2.5, border: `1px solid ${theme.cardBorder}`, borderRadius: '12px', bgcolor: theme.bg }}>
                      <Box display="flex" justifyContent="space-between" flexWrap="wrap" alignItems="center" sx={{ mb: 1.5 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                          {review.studentName}
                        </Typography>
                        <Typography variant="caption" sx={{ color: theme.secondaryText }}>
                          {new Date(review.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </Typography>
                      </Box>
                      
                      <Box display="flex" alignItems="center" sx={{ mb: 1.5 }}>
                        <Rating value={review.rating} readOnly size="small" sx={{ mr: 1 }} />
                        {review.isPassedPracticalTest && (
                          <Box display="flex" alignItems="center" sx={{ bgcolor: '#d1e7dd', px: 1.5, py: 0.2, borderRadius: '12px' }}>
                            <CheckCircleIcon sx={{ color: '#0f5132', fontSize: 14, mr: 0.5 }} />
                            <Typography variant="caption" sx={{ color: '#0f5132', fontWeight: 'bold' }}>
                              PASSED TEST
                            </Typography>
                          </Box>
                        )}
                      </Box>

                      <Typography variant="body2" sx={{ lineHeight: 1.6, fontStyle: 'italic' }}>
                        "{review.comment}"
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </Card>
          </Grid>

          {/* Right Column: Lead Capture Booking Inquiry Form */}
          <Grid size={{ xs: 12, md: 5 }}>
            <Card
              elevation={8}
              sx={{
                p: 4,
                borderRadius: '24px',
                border: `1px solid ${theme.cardBorder}`,
                bgcolor: theme.cardBg,
                position: 'sticky',
                top: 24,
                boxShadow: '0 12px 40px rgba(0,0,0,0.06)'
              }}
            >
              <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1 }}>
                Book Lessons
              </Typography>
              <Typography variant="body2" sx={{ color: theme.secondaryText, mb: 3 }}>
                Submit a booking inquiry below. ADI {profile.firstName} will contact you to schedule lessons and arrange payment.
              </Typography>

              {submitSuccess ? (
                <Box textAlign="center" py={2}>
                  <Alert severity="success" sx={{ mb: 3, borderRadius: '12px' }}>
                    {submitSuccess}
                  </Alert>
                  <Button variant="outlined" onClick={() => setSubmitSuccess(null)} fullWidth>
                    Submit Another Inquiry
                  </Button>
                </Box>
              ) : (
                <form onSubmit={handleInquirySubmit}>
                  {submitError && (
                    <Alert severity="error" sx={{ mb: 2.5, borderRadius: '12px' }}>
                      {submitError}
                    </Alert>
                  )}

                  <Box display="flex" flexDirection="column" gap={2.5}>
                    <TextField
                      label="Your Full Name"
                      required
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      fullWidth
                    />

                    <TextField
                      label="Email Address"
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      fullWidth
                    />

                    <TextField
                      label="Phone Number"
                      required
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      placeholder="e.g. 07123456789"
                      fullWidth
                    />

                    <FormControl fullWidth>
                      <InputLabel id="transmission-label">Preferred Transmission</InputLabel>
                      <Select
                        labelId="transmission-label"
                        value={transmission}
                        onChange={(e) => setTransmission(e.target.value)}
                        label="Preferred Transmission"
                      >
                        <MenuItem value="Manual">Manual</MenuItem>
                        <MenuItem value="Automatic">Automatic</MenuItem>
                      </Select>
                    </FormControl>

                    <FormControl fullWidth>
                      <InputLabel id="experience-label">Your Driving Experience</InputLabel>
                      <Select
                        labelId="experience-label"
                        value={experienceLevel}
                        onChange={(e) => setExperienceLevel(e.target.value)}
                        label="Your Driving Experience"
                      >
                        <MenuItem value="Beginner">Beginner (No experience)</MenuItem>
                        <MenuItem value="SomeLessons">Some Lessons (Partly trained)</MenuItem>
                        <MenuItem value="TestReady">Near Test Ready (Mock tests)</MenuItem>
                        <MenuItem value="FailedTest">Failed Test (Refresher needed)</MenuItem>
                      </Select>
                    </FormControl>

                    <TextField
                      label="Message / Requirements (Optional)"
                      multiline
                      rows={3}
                      placeholder="Share your availability, preferred test centre, or questions..."
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      fullWidth
                    />

                    <Button
                      type="submit"
                      variant="contained"
                      disabled={submitting}
                      sx={{
                        py: 2,
                        borderRadius: '14px',
                        fontWeight: 'bold',
                        textTransform: 'none',
                        fontSize: '16px',
                        background: `linear-gradient(135deg, ${theme.primary} 0%, ${theme.primaryHover} 100%)`,
                        color: theme.btnText,
                        '&:hover': {
                          background: theme.primaryHover
                        }
                      }}
                    >
                      {submitting ? 'Submitting...' : `Request Booking (£${profile.baseHourlyRate}/hr)`}
                    </Button>
                  </Box>
                </form>
              )}
            </Card>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}
